import { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeContext = createContext({
  mode: "dark",
  resolved: "dark",
  setMode: () => {},
});

const STORAGE_KEY = "brasserie-theme-mode";

function applyTheme(resolved) {
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.setAttribute("data-theme", resolved);
}

function getSystemTheme() {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeProvider({ children }) {
  // Default mode: dark (user choice)
  const [mode, setModeState] = useState(() => {
    if (typeof window === "undefined") return "dark";
    return localStorage.getItem(STORAGE_KEY) || "dark";
  });
  const [resolved, setResolved] = useState(() =>
    mode === "system" ? getSystemTheme() : mode
  );

  // Apply theme whenever resolved value changes
  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  // Recompute resolved when mode changes
  useEffect(() => {
    if (mode === "system") {
      setResolved(getSystemTheme());
    } else {
      setResolved(mode);
    }
  }, [mode]);

  // Listen to OS preference changes when mode === "system"
  useEffect(() => {
    if (mode !== "system" || typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => setResolved(e.matches ? "dark" : "light");
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [mode]);

  const setMode = useCallback((next) => {
    setModeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch (_) {}
  }, []);

  return (
    <ThemeContext.Provider value={{ mode, resolved, setMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
